import { ALARM_LEVEL_COLORS, hasPermission, type AccessibleTenant, type AlarmEventDto, type AlarmLevel, type PermissionSet } from '@atnt/shared';

/* ───────────── URL (search.ts) ───────────── */

export { ALARM_STATUS_FILTERS, ALARM_TABS, alarmsSearchSchema, type AlarmTab, type AlarmsSearch } from './search';

/* ───────────── permissions (apps/api/src/modules/alarms/routes.ts) ───────────── */

const VIEW_MENUS = ['dashboard', 'sections', 'charts'] as const;
const viewAny = (p: PermissionSet) => VIEW_MENUS.some((menu) => hasPermission(p, { panel: 'monitoring', menu, action: 'view' }));

/** Events are listed for every monitoring viewer (dashboard, sections or charts). */
export const canViewAlarms = (tenant: AccessibleTenant) => tenant.isAdmin || viewAny(tenant.permissions);
/** "Tasdiqlash" needs monitoring `dashboard.edit`. */
export const canAckAlarms = (tenant: AccessibleTenant) =>
  tenant.isAdmin || hasPermission(tenant.permissions, { panel: 'monitoring', menu: 'dashboard', action: 'edit' });
/** Sound settings belong to reservoir admins. */
export const canEditAlarmSettings = (tenant: AccessibleTenant) => tenant.isAdmin;

/* ───────────── levels (14:20932) ───────────── */

/** Level pill colours of the legacy design: Normal `#35A45E`, M-1 `#EF9944`, M-2 `#EC3538` (fill @30 %). */
export const LEVEL_COLOR: Record<AlarmLevel | 'normal', string> = { normal: '#35a45e', m1: ALARM_LEVEL_COLORS.m1, m2: ALARM_LEVEL_COLORS.m2 };

/* ───────────── values & durations ───────────── */

export function formatValue(value: number | null, event: Pick<AlarmEventDto, 'indicator'>, locale: string): string {
  if (value == null) return '—';
  const decimals = event.indicator?.precision ?? null;
  const text = decimals != null ? value.toFixed(Math.max(0, Math.min(6, decimals))) : String(Math.round(value * 100) / 100);
  const sep = locale === 'ru' ? ',' : '.';
  const unit = event.indicator?.unit;
  return `${text.replace('.', sep).replace(/^-/, '−')}${unit ? ` ${unit}` : ''}`;
}

/** Duration parts, largest two: { d, h } · { h, m } · { m }. */
export function durationParts(ms: number): Array<['d' | 'h' | 'm', number]> {
  const minutes = Math.max(0, Math.floor(ms / 60_000));
  const d = Math.floor(minutes / 1440);
  const h = Math.floor((minutes % 1440) / 60);
  const m = minutes % 60;
  if (d) return h ? [['d', d], ['h', h]] : [['d', d]];
  if (h) return m ? [['h', h], ['m', m]] : [['h', h]];
  return [['m', m]];
}

export const indicatorTitle = (e: AlarmEventDto) => (e.indicator ? `${e.indicator.deviceCode}  ${e.indicator.name}` : '—');
